import React, { useState, useRef, useCallback } from 'react';
import { Loader2, Check, X, ZoomIn, ZoomOut } from 'lucide-react';

const OUTPUT_SIZE = 300;
const MAX_ZOOM_FACTOR = 4;

// 頭像裁切視窗：拖曳移動、雙指捏合或滑桿縮放，確定後輸出 300x300 JPEG blob
export default function AvatarCropModal({ imageUrl, isProcessing, onCancel, onConfirm }) {
  const [viewport] = useState(() => Math.min(window.innerWidth - 64, 300));
  const [imgSize, setImgSize] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);
  const minScaleRef = useRef(1);

  const clampOffset = useCallback((off, s, size) => {
    if (!size) return off;
    const maxX = Math.max(0, (size.w * s - viewport) / 2);
    const maxY = Math.max(0, (size.h * s - viewport) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, off.x)),
      y: Math.min(maxY, Math.max(-maxY, off.y))
    };
  }, [viewport]);

  const handleImageLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.target;
    const minScale = Math.max(viewport / w, viewport / h);
    minScaleRef.current = minScale;
    setImgSize({ w, h });
    setScale(minScale);
    setOffset({ x: 0, y: 0 });
  };

  // 以視窗中心為基準縮放，並讓位移等比例跟著縮放
  const applyScale = useCallback((newScale) => {
    const minScale = minScaleRef.current;
    const clamped = Math.min(minScale * MAX_ZOOM_FACTOR, Math.max(minScale, newScale));
    setScale(prevScale => {
      setOffset(prevOffset => clampOffset({
        x: prevOffset.x * (clamped / prevScale),
        y: prevOffset.y * (clamped / prevScale)
      }, clamped, imgSize));
      return clamped;
    });
  }, [clampOffset, imgSize]);

  const snapshotGesture = (currentOffset, currentScale) => {
    gestureRef.current = {
      offset: currentOffset,
      scale: currentScale,
      points: new Map(pointersRef.current)
    };
  };

  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    snapshotGesture(offset, scale);
  };

  const handlePointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId) || !gestureRef.current) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    const start = gestureRef.current;
    const points = [...pointersRef.current.entries()];

    if (points.length === 1) {
      const [id, current] = points[0];
      const startPoint = start.points.get(id);
      if (!startPoint) return;
      setOffset(clampOffset({
        x: start.offset.x + (current.x - startPoint.x),
        y: start.offset.y + (current.y - startPoint.y)
      }, scale, imgSize));
    } else if (points.length >= 2) {
      const [idA, a] = points[0];
      const [idB, b] = points[1];
      const startA = start.points.get(idA);
      const startB = start.points.get(idB);
      if (!startA || !startB) return;
      const startDist = Math.hypot(startA.x - startB.x, startA.y - startB.y);
      const currentDist = Math.hypot(a.x - b.x, a.y - b.y);
      if (startDist > 0) {
        applyScale(start.scale * (currentDist / startDist));
      }
    }
  };

  const handlePointerEnd = (e) => {
    pointersRef.current.delete(e.pointerId);
    snapshotGesture(offset, scale);
  };

  const handleConfirm = () => {
    const img = imgRef.current;
    if (!img || !imgSize || isProcessing) return;

    const srcSize = viewport / scale;
    const sx = imgSize.w / 2 - offset.x / scale - srcSize / 2;
    const sy = imgSize.h / 2 - offset.y / scale - srcSize / 2;

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    canvas.getContext('2d').drawImage(img, sx, sy, srcSize, srcSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    canvas.toBlob((blob) => {
      if (blob) onConfirm(blob);
    }, 'image/jpeg', 0.85);
  };

  const minScale = minScaleRef.current;
  const sliderValue = imgSize ? ((scale - minScale) / (minScale * (MAX_ZOOM_FACTOR - 1))) * 100 : 0;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 1000,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '20px',
        width: '100%',
        maxWidth: `${viewport + 48}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>調整照片</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
            拖曳移動照片，捏合或使用滑桿縮放
          </div>
        </div>

        {/* 裁切區域 */}
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          style={{
            position: 'relative',
            width: `${viewport}px`,
            height: `${viewport}px`,
            overflow: 'hidden',
            borderRadius: '16px',
            backgroundColor: '#111827',
            touchAction: 'none',
            cursor: 'grab',
            userSelect: 'none'
          }}
        >
          <img
            ref={imgRef}
            src={imageUrl}
            alt="裁切預覽"
            draggable={false}
            onLoad={handleImageLoad}
            style={imgSize ? {
              position: 'absolute',
              left: `${viewport / 2 + offset.x - (imgSize.w * scale) / 2}px`,
              top: `${viewport / 2 + offset.y - (imgSize.h * scale) / 2}px`,
              width: `${imgSize.w * scale}px`,
              height: `${imgSize.h * scale}px`,
              maxWidth: 'none',
              pointerEvents: 'none'
            } : { opacity: 0 }}
          />
          {/* 圓形遮罩 */}
          <div style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
            border: '2px solid rgba(255, 255, 255, 0.9)',
            pointerEvents: 'none'
          }} />
        </div>

        {/* 縮放滑桿 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          width: '100%',
          padding: '0 4px'
        }}>
          <ZoomOut size={18} color="#6b7280" />
          <input
            type="range"
            min="0"
            max="100"
            value={sliderValue}
            disabled={!imgSize}
            onChange={(e) => {
              const ratio = Number(e.target.value) / 100;
              applyScale(minScale + ratio * minScale * (MAX_ZOOM_FACTOR - 1));
            }}
            style={{ flex: 1, accentColor: '#4e8bca' }}
          />
          <ZoomIn size={18} color="#6b7280" />
        </div>

        {/* 操作按鈕 */}
        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '12px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white',
              color: '#6b7280',
              fontSize: '14px',
              fontWeight: '600',
              cursor: isProcessing ? 'not-allowed' : 'pointer'
            }}
          >
            <X size={16} />
            取消
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!imgSize || isProcessing}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '12px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: '#4e8bca',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: (!imgSize || isProcessing) ? 'not-allowed' : 'pointer',
              opacity: (!imgSize || isProcessing) ? 0.7 : 1
            }}
          >
            {isProcessing ? (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Check size={16} />
            )}
            {isProcessing ? '上傳中...' : '確定'}
          </button>
        </div>
      </div>
    </div>
  );
}
